import { request } from '@app/http/request'
import { appConfig } from '@app/shared/config'
import type { SpResponse } from '@backend-models/common/request/sp-response'
import type { WordReplacementDictionaryDto } from '@backend-models/consortiums/word-replacement/word-replacement-dictionary-dto'
import { BehaviorSubject, firstValueFrom } from 'rxjs'

export type WordReplacementDictionary = WordReplacementDictionaryDto | null

// Word replacement dictionary subject which starts null
const $wordReplacementDictionary = new BehaviorSubject<WordReplacementDictionary>(null)

// Word replacement dictionary observable for subscriptions
const $wordReplacementDictionaryObservable = $wordReplacementDictionary.asObservable()

/**
 * Update word replacement dictionary
 * @param dictionary
 */
export const updateWordReplacementDictionary = (dictionary: WordReplacementDictionary) => {
  $wordReplacementDictionary.next(dictionary)
  // Clear cache in word-replacement-text utility when dictionary updates
  // Use dynamic import to avoid circular dependency
  import('@app/utils/word-replacement-text').then((module) => {
    module.clearReplacementCache()
  })
}

/**
 * Get word replacement dictionary observable to listen for changes
 * @returns Observable
 */
export const getWordReplacementDictionaryObservable = () => $wordReplacementDictionaryObservable

/**
 * Get current word replacement dictionary synchronously
 * @returns Current dictionary or null
 */
export const getWordReplacementDictionarySync = (): WordReplacementDictionary => {
  // Access BehaviorSubject's current value directly
  return $wordReplacementDictionary.value
}

/**
 * Get current word replacement dictionary
 * @returns Promise
 */
export const getWordReplacementDictionary = () => firstValueFrom(getWordReplacementDictionaryObservable())

/**
 * Load word replacement dictionary
 */
export const loadWordReplacementDictionary = async () => {
  try {
    const req = await request(`${appConfig.api}/consortiums/word-replacement-dictionary`, { auth: true, hideLoading: true })
    const res = (await req.json()) as SpResponse<WordReplacementDictionaryDto>
    if (res.success && res.data) {
      updateWordReplacementDictionary(res.data)
    } else {
      updateWordReplacementDictionary({ replacements: [] })
    }
  } catch (error) {
    console.error('Could not load word replacement dictionary', error)
    updateWordReplacementDictionary({ replacements: [] })
  }
}

